import _ from 'lodash';
import PropTypes from 'prop-types';
import { Fragment, memo, useCallback } from 'react';
import NumberFormat from 'react-number-format';
import { Tooltip, withStyles } from '@material-ui/core';
import { useSelector } from 'react-redux';
import { RiInformationLine } from 'react-icons/ri';
import { SearchStatus } from '../../../../constants';
import withSpinner from '../../../../HOCs/withSpinner';
import currencySymbol from '../../../../helpers/currencySymbol';
import parseRentalCarCharges from '../parseRentalCarCharges';

const LightTooltip = withStyles((theme) => ({
  tooltip: {
    fontFamily: 'var(--fontFamilyBody)',
    backgroundColor: theme.palette.common.white,
    color: 'rgba(0, 0, 0, 0.87)',
    boxShadow: theme.shadows[3],
    fontSize: 11,
    maxWidth: '350px',
  },
  arrow: {
    color: theme.palette.common.white,
  },
}))(Tooltip);

const CarDetails = ({
  itinerary, getCarRateRulesStatus, vendorMessages, doShowMarkupRate,
}) => {
  const { travelAgency } = useSelector((state) => state.user);
  const markupAmount = itinerary.priceInfo.markupPriceAllDays > 0
    ? +(itinerary.priceInfo.markupPriceAllDays - itinerary.priceInfo.priceAllDays).toFixed(2) : 0;
  const {
    baseRate, approxTotalRate, taxesAndFees, otherCharges, doShowTaxesAndFees,
  } = parseRentalCarCharges(itinerary.priceInfo && itinerary.priceInfo.vehicleCharges);

  const serviceFeesElement = doShowMarkupRate && itinerary.priceInfo && markupAmount > 0
    ? (
      <div className="flex">
        <b className="flex items-center">
          Service fee
          {travelAgency && travelAgency.settings
            && travelAgency.settings.taxesAndFeesToolTipText && (
              <LightTooltip title={travelAgency.settings.taxesAndFeesToolTipText} placement="bottom" arrow className="ml-1">
                <div><RiInformationLine size={18} className="cursor-pointer" /></div>
              </LightTooltip>
          )}
          :
        </b>
        <NumberFormat
          value={markupAmount}
          prefix={currencySymbol(itinerary.priceInfo.currencyCode)}
          displayType="text"
          thousandSeparator
          fixedDecimalScale="true"
          decimalScale="2"
          className="ml-1"
        />
      </div>
    ) : <></>;

  const displayCharge = useCallback((vehicleCharge) => (
    <div key={vehicleCharge.chargeType + vehicleCharge.amount}>
      <b>
        {vehicleCharge.chargeType}
        :
      </b>
      {' '}
      {vehicleCharge.markupAmount === 0 && vehicleCharge.amount === 0
        ? <span>N/A</span>
        : (
          <NumberFormat
            value={doShowMarkupRate && vehicleCharge.markupAmount > 0
              ? vehicleCharge.markupAmount : vehicleCharge.amount}
            prefix={currencySymbol(itinerary.priceInfo.currencyCode)}
            displayType="text"
            thousandSeparator
            fixedDecimalScale="true"
            decimalScale="2"
          />
        )}
    </div>
  ), [doShowMarkupRate, itinerary.priceInfo.currencyCode]);

  return (
    <div className="col-span-6">
      <div className="mt-5">
        <b>Vehicle Charges:</b>
        {' '}
        <br />
        {doShowTaxesAndFees ? (
          <>
            {displayCharge(baseRate)}
            {displayCharge({ chargeType: 'Taxes and Fees', amount: taxesAndFees, markupAmount: taxesAndFees })}
            {serviceFeesElement}
            <hr className="my-1 w-64" />
            {displayCharge(approxTotalRate)}
          </>
        ) : <></>}
        {doShowTaxesAndFees && otherCharges && otherCharges.length ? (
          <div className="mt-5">
            <div className="font-bold">Other Charges:</div>
            {otherCharges.map((vehicleCharge) => (displayCharge(vehicleCharge)))}
          </div>
        ) : <></>}

        {!doShowTaxesAndFees && itinerary.priceInfo.vehicleCharges
          && itinerary.priceInfo.vehicleCharges.map((vehicleCharge, index) => (
            <Fragment key={vehicleCharge.chargeType + vehicleCharge.amount}>
              {index === itinerary.priceInfo.vehicleCharges.length - 1 && serviceFeesElement}
              {displayCharge(vehicleCharge)}
            </Fragment>
          ))}
      </div>

      {withSpinner(() => (
        vendorMessages && Object.keys(vendorMessages).length > 0 && (
          <div className="mt-5">
            {_.orderBy(Object.keys(vendorMessages), [], ['desc']).map((key) => (
              <div className="whitespace-normal" key={key + vendorMessages[key]}>
                <b>
                  {key}
                  {' '}
                </b>
                <br />
                <pre className="font-body ml-5 whitespace-pre-wrap">{vendorMessages[key]}</pre>
              </div>
            ))}
          </div>
        )
      ), getCarRateRulesStatus === SearchStatus.SEARCHING, 'Loading vendor messages...')}
    </div>
  );
};

CarDetails.propTypes = {
  itinerary: PropTypes.objectOf(PropTypes.objectOf),
  getCarRateRulesStatus: PropTypes.number,
  vendorMessages: PropTypes.objectOf(PropTypes.objectOf),
  doShowMarkupRate: PropTypes.bool,
};

CarDetails.defaultProps = {
  itinerary: {},
  vendorMessages: {},
  getCarRateRulesStatus: 0,
  doShowMarkupRate: false,
};

export default memo(CarDetails);
